import { Component, inject } from '@angular/core';
import { Router, RouterOutlet, NavigationStart } from '@angular/router';
import { ItBackToTopComponent, ItNotificationsComponent } from 'design-angular-kit';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { filter } from 'rxjs/operators';
import { ProjectsService } from './shared/services/projects.service';
import { UtilsService } from './shared/services/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { VariantsService } from './shared/services/variants.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    ItBackToTopComponent,
    ItNotificationsComponent,
  ],
  templateUrl: './app.html',
})
export class App {
  private readonly router = inject(Router);
  private readonly _projectsService: ProjectsService = inject(ProjectsService);
  private readonly _variantsService: VariantsService = inject(VariantsService);
  private readonly _utilsService: UtilsService = inject(UtilsService);
  private readonly _translateService: TranslateService = inject(TranslateService);

  constructor() {
    this._translateService.setDefaultLang(localStorage.getItem('lang') ?? 'it');
    this._translateService.use(localStorage.getItem('lang') ?? 'it');

    // Subscribe to router events to track URL changes and extract data
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: any) => {
        this._utilsService.setCurrentUrl(event.url);
        const segments = event.url.split('/').filter(Boolean);

        const projectIndex = segments.indexOf('projects');
        const buildingIndex = segments.indexOf('buildings');
        const awardIndex = segments.findIndex((segment: string) => segment.endsWith('awards'));
        const variantIndex = segments.indexOf('variants');
        const fundingLineIndex = segments.indexOf('funding-lines');
        const forwardingBlockIndex = segments.indexOf('forwarding-blocks');
        const tenderIndex = segments.indexOf('calls-for-tender');
        const lotIndex = segments.indexOf('lots');
        const configuratorIndex = segments.indexOf('configurator');
        const economicFrameworkIndex = segments.indexOf('economic-framework');

        const projectId = this.parseId(segments[projectIndex + 1]);
        const buildingId = this.parseId(segments[buildingIndex + 1]);
        const awardId = this.parseId(segments[awardIndex + 1]);
        const variantId = this.parseId(segments[variantIndex + 1]);
        const fundingLineId = this.parseId(segments[fundingLineIndex + 1]);
        const forwardingBlockId = this.parseId(segments[forwardingBlockIndex + 1]);
        const tenderId = this.parseId(segments[tenderIndex + 1]);
        const lotId = this.parseId(segments[lotIndex + 1]);
        const configuratorId = this.parseId(segments[configuratorIndex + 1]);
        const economicFrameworkId = this.parseId(segments[economicFrameworkIndex + 1]);

        this._utilsService.setProjectId(projectId);
        this._utilsService.setBuildingId(buildingId);
        this._utilsService.setAwardId(awardId);
        this._utilsService.setVariantId(variantId);
        this._utilsService.setFundingLineId(fundingLineId);
        this._utilsService.setForwardingBlockId(forwardingBlockId);
        this._utilsService.setTenderId(tenderId);
        this._utilsService.setLotId(lotId);
        this._utilsService.setConfiguratorId(configuratorId);
        this._utilsService.setEconomicFrameworkId(economicFrameworkId);

        if (
          projectId &&
          (!this._utilsService.project() || projectId != this._utilsService.project()?.id)
        ) {
          this._projectsService.updateCurrentProject();
        }

        if (
          variantId &&
          (!this._utilsService.variant() || variantId != this._utilsService.variant()?.id)
        ) {
          this._variantsService.updateCurrentVariant();
        }
      });
  }

  /**
   * Checks if a string is a valid GUID.
   */
  private isGuid(str: string): boolean {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
  }

  /**
   * Converts a segment into a valid ID string (number or GUID), otherwise returns null.
   */
  private parseId(segment?: string | null): string | null {
    if (!segment) return null;
    if (!Number.isNaN(Number(segment))) return segment; // number as string
    if (this.isGuid(segment)) return segment; // valid GUID
    return null;
  }
}
